import React, { FC } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import Badge from '@mui/material/Badge';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import hubLogo from '../../Assets/branding/hub-logo';
import { AccountMenu } from '../AccountMenu/account-menu.component';
import { Menu as AppMenu  } from '../Menu/menu.component';
import { NotificationMenu } from '../NotificationMenu/notification-menu.component';
import api, { adminVersion } from '../../Services/api';
import { useAuth } from '../../Hooks/Auth';
import { Flex } from '../Flex/flex.component';
import { ListNotificationsUseCase } from '../../Usecase/Notifications/list-notifications-usecase';
import { NotificationsApiRemoteService } from '../../Services/api/notifications/notifications-service';
import { MarkAsReadNotificationsUseCase } from '../../Usecase/Notifications/mark-as-read-notifications-usecase';

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: '#FFFFFF',
  border: `1px solid ${theme.palette.primary.main}`,
  '&:hover': {
    backgroundColor: '#FFFFFF',
    border: `1px solid ${theme.palette.primary.main}`,
  },
  marginRight: theme.spacing(2),
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(3),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.primary.main,
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: theme.palette.text.primary,
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    '&::placeholder': {
      color: theme.palette.grey[500],
      opacity: 1,
    },
    [theme.breakpoints.up('md')]: {
      width: '20ch',
    },
  },
}));

const Bar = () => {
  const listNotificationsUseCase = new ListNotificationsUseCase(new NotificationsApiRemoteService(api))
  const markAsReadNotificationUseCase = new MarkAsReadNotificationsUseCase(new NotificationsApiRemoteService(api))
  
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  const handleDrawerToggle = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const menuId = 'primary-search-account-menu';
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={menuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
      <MenuItem onClick={handleMenuClose}>My account</MenuItem>
    </Menu>
  );

  const mobileMenuId = 'primary-search-account-menu-mobile';
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      id={mobileMenuId}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      {/* <MenuItem>
        <IconButton size="large" aria-label="show 4 new mails" color="primary">
          <Badge badgeContent={4} color="error">
            <MailOutlinedIcon color="primary" />
          </Badge>
        </IconButton>
        <p>Message</p>
      </MenuItem> */}
      <MenuItem>
        <IconButton
          size="large"
          aria-label="show 17 new notifications"
          color="primary"
        >
          <Badge badgeContent={17} color="error">
            <NotificationsOutlinedIcon color="primary" />
          </Badge>
        </IconButton>
        <p>Notifications</p>
      </MenuItem>
      <MenuItem onClick={handleProfileMenuOpen}>
        <IconButton
          size="large"
          aria-label="account of current user"
          aria-controls="primary-search-account-menu"
          aria-haspopup="true"
          color="primary"
        >
          <AccountCircleOutlinedIcon color="primary" />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  return (
    <>
    <AppMenu open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
    <Box sx={{ flexShrink: 0 }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: '#FFF',
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="primary"
            aria-label="Abrir menu"
            sx={{ mr: 2 }}
            onClick={handleDrawerToggle}
          >
            <MenuIcon color="primary" />
          </IconButton>
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/dashboard')}
            aria-label="Ir para o início do HUB"
            sx={{
              border: 0,
              p: 0,
              m: 0,
              background: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Box
              component="img"
              src={hubLogo}
              alt="Papello HUB"
              sx={{ width: { xs: 140, sm: 180 }, height: 'auto', display: 'block' }}
            />
          </Box>
          {/* <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              placeholder="Search…"
              inputProps={{ 'aria-label': 'search' }}
            />
          </Search> */}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: 'none', md: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' } }}>
            {/* <IconButton size="large" aria-label="show 4 new mails" color="primary">
              <Badge badgeContent={4} color="error">
                <MailOutlinedIcon color="primary" />
              </Badge>
            </IconButton> */}
            <Flex sx={{ height: '100%', alignItems: 'center', display: 'flex', justifyContent: 'center'}}>
              <Typography fontSize={12} fontWeight={600} color="text.secondary" sx={{ marginRight: 2}}>Versão: {adminVersion}</Typography>
            </Flex>
            
            <NotificationMenu listNotificationsUseCase={listNotificationsUseCase} markAsReadNotificationUseCase={markAsReadNotificationUseCase} />
            <AccountMenu />
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="show more"
              aria-controls={mobileMenuId}
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="primary"
            >
              <MoreIcon color="primary" />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      {renderMobileMenu}
      {renderMenu}
    </Box>
    </>
    
  );
}

export { Bar}